import { Phone, Mail, Globe, MessageCircle } from "lucide-react";
import { PlatformIcon } from "@/features/identity/components/PlatformIcon";
import { getPlatform } from "@/features/identity/constants/platforms";

export const hostOf = (url) => {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
};
const telHref = (p) => `tel:${p.replace(/[^\d+]/g, "")}`;

export function Hero({ identity: id }) {
  return (
    <header className={`pp-hero ${id.photoUrl ? "pp-hero--photo" : ""}`}>
      {id.photoUrl && <img src={id.photoUrl} alt={id.fullName || ""} className="pp-hero-img" data-testid="pp-hero-photo" />}
      <div className="pp-hero-text">
        <p className="pp-eyebrow">Digital Business Profile</p>
        {id.fullName && <h1 data-testid="pp-name" className="pp-name">{id.fullName}</h1>}
        {id.title && <p data-testid="pp-title" className="pp-title">{id.title}</p>}
        {id.company && <p data-testid="pp-company" className="pp-company">{id.company}</p>}
      </div>
    </header>
  );
}

export function SocialButtons({ links }) {
  if (!links.length) return null;
  return (
    <ul className="pp-social" data-testid="pp-social">
      {links.map((l) => (
        <li key={l.platform}>
          <a href={l.url} target="_blank" rel="noopener noreferrer" aria-label={getPlatform(l.platform).label} data-testid={`pp-social-${l.platform}`}>
            <PlatformIcon platform={l.platform} className="h-5 w-5" />
          </a>
        </li>
      ))}
    </ul>
  );
}

export function ContactActions({ identity: id }) {
  const wa = id.links.find((l) => l.platform === "whatsapp");
  const actions = [
    id.phone && { key: "call", label: "Call", Icon: Phone, href: telHref(id.phone) },
    id.email && { key: "email", label: "Email", Icon: Mail, href: `mailto:${id.email}` },
    wa && { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle, href: wa.url, external: true },
    id.website && { key: "website", label: "Website", Icon: Globe, href: id.website, external: true },
  ].filter(Boolean);
  if (!actions.length) return null;
  return (
    <div className="pp-actions" style={{ gridTemplateColumns: `repeat(${actions.length}, minmax(0, 1fr))` }}>
      {actions.map(({ key, label, Icon, href, external }) => (
        <a key={key} href={href} data-testid={`pp-action-${key}`} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
          <Icon className="h-5 w-5" aria-hidden="true" />
          <span>{label}</span>
        </a>
      ))}
    </div>
  );
}

export const Panel = ({ title, testId, children }) => (
  <section className="pp-panel" data-testid={testId}>
    {title && <h2 className="pp-panel-title">{title}</h2>}
    {children}
  </section>
);

export function CompanyPanel({ identity: id }) {
  if (!id.company && !id.logoUrl) return null;
  return (
    <Panel testId="pp-company-panel">
      <div className="pp-company-row">
        {id.logoUrl && <span className="pp-logo"><img src={id.logoUrl} alt={id.company || "Company logo"} /></span>}
        <div className="min-w-0">
          {id.company && <p className="pp-company-name">{id.company}</p>}
          {id.website && <a className="pp-link" href={id.website} target="_blank" rel="noopener noreferrer">{hostOf(id.website)}</a>}
        </div>
      </div>
    </Panel>
  );
}

export function ContactPanel({ identity: id }) {
  const rows = [
    id.phone && { key: "phone", Icon: Phone, text: id.phone, href: telHref(id.phone) },
    id.email && { key: "email", Icon: Mail, text: id.email, href: `mailto:${id.email}` },
    id.website && { key: "website", Icon: Globe, text: hostOf(id.website), href: id.website, external: true },
  ].filter(Boolean);
  if (!rows.length) return null;
  return (
    <Panel title="Contact details" testId="pp-contact-panel">
      <ul className="pp-contact-list">
        {rows.map(({ key, Icon, text, href, external }) => (
          <li key={key}>
            <Icon className="pp-contact-icon" aria-hidden="true" />
            <a className="pp-link" href={href} data-testid={`pp-contact-${key}`} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{text}</a>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
